import { useCallback, useMemo, useState } from "react";

import { ROWS_PER_PAGE } from "@covertree/consts";
import {
  GetResidentsFilterStatus,
  GetResidentsSortBy,
  SortOrder,
} from "@graphql/api";
import { useTableViewPagination } from "@covertree/hooks";
import { useGetResidentsQuery } from "@redux/residents/residentsApi";
import { calculateTotalCount } from "@covertree/utils";

import { useGetResidentsColumns } from "./useGetResidentsColumns";
import { useGetResidentsFilters } from "./useGetResidentsFilters";
import { useMappedResidents } from "./useMappedResidents";
import { useAuthContext } from "@covertree/auth";
import CognitoUserGroup from "@enums/CognitoUserGroup";

export const useGetResidents = () => {
  const { hasGroup } = useAuthContext();
  const filters = useGetResidentsFilters();
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);
  const {
    from,
    order,
    sortBy,
    filter,
    currentPage,
    onChangePage,
    onChangeFilter,
    onChangeSort,
  } = useTableViewPagination(0, rowsPerPage, {
    from: "0",
    filter: filters[0].value,
    order: SortOrder.Descending,
    sortBy: GetResidentsSortBy.UpdatedDate,
  });

  const handleSortChange = useCallback(
    (nextSortBy: string) => () => {
      onChangeSort(nextSortBy);
    },
    [onChangeSort]
  );

  const queryVariables = useMemo(
    () => ({
      pagination: {
        from,
        size: rowsPerPage,
      },
      sorting: {
        sort: order,
        sortBy: sortBy as GetResidentsSortBy,
      },
      filter: {
        status: filter as GetResidentsFilterStatus,
      },
    }),
    [filter, from, order, rowsPerPage, sortBy]
  );

  const { data, isFetching, isError } = useGetResidentsQuery(queryVariables, {
    refetchOnMountOrArgChange: true,
  });

  const columns = useGetResidentsColumns(
    filter as GetResidentsFilterStatus,
    hasGroup([
      CognitoUserGroup.CommunityManager,
      CognitoUserGroup.CommunityPortfolioExecutive,
    ])
  );

  const residents = useMappedResidents(data?.items, columns);

  return {
    residents,
    loading: isFetching,
    error: isError,
    columns,
    filters,
    order,
    sortBy,
    currentPage,
    rowsPerPage,
    totalCount: calculateTotalCount(data),
    handleSortChange,
    handleChangePage: onChangePage,
    handleFilterChange: onChangeFilter,
    handleRowsPerPageChange: setRowsPerPage,
  };
};
